import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Save } from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get('/settings');
        setSettings(data);
        const vals = {};
        data.forEach(s => { vals[s.key] = s.value; });
        setValues(vals);
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
      finally { setLoading(false); }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      const payload = Object.entries(values).map(([key, value]) => ({ key, value: String(value) }));
      await api.put('/settings', payload);
      toast.success('Settings saved');
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Settings</h1>
          <p className="text-sm text-brand-text-secondary mt-1">System configuration and preferences</p>
        </div>
        <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="save-settings-btn">
          <Save size={16} className="mr-2" /> Save Changes
        </Button>
      </div>

      <Card className="border border-brand-border bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-brand-text-primary">General Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-brand-text-secondary text-sm">Loading settings...</p>
          ) : settings.length === 0 ? (
            <p className="text-brand-text-secondary text-sm">No settings found</p>
          ) : (
            settings.map(s => (
              <div key={s.key} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                <Label className="text-sm font-medium text-brand-text-primary">{s.label || s.key}</Label>
                <div className="sm:col-span-2">
                  {s.type === 'boolean' ? (
                    <select
                      value={values[s.key] || 'false'}
                      onChange={e => setValues({...values, [s.key]: e.target.value})}
                      className="w-full h-10 px-3 bg-white border border-brand-border rounded-sm text-sm focus:ring-1 focus:ring-brand-primary focus:border-brand-primary"
                      data-testid={`setting-${s.key}`}
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </select>
                  ) : (
                    <Input
                      type={s.type === 'number' ? 'number' : 'text'}
                      value={values[s.key] || ''}
                      onChange={e => setValues({...values, [s.key]: e.target.value})}
                      className="bg-white border-brand-border rounded-sm"
                      data-testid={`setting-${s.key}`}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card className="border border-brand-border bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-brand-text-primary">System Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex justify-between py-2 border-b border-brand-border/50">
            <span className="text-brand-text-secondary">Version</span>
            <span className="text-brand-text-primary font-medium">1.0.0</span>
          </div>
          <div className="flex justify-between py-2 border-b border-brand-border/50">
            <span className="text-brand-text-secondary">Platform</span>
            <span className="text-brand-text-primary font-medium">Quantora Fund Management</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-brand-text-secondary">Database</span>
            <span className="text-brand-text-primary font-medium">MongoDB</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
